import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Store,
  Gift,
  Users,
  Building,
  BarChart3,
  TrendingUp,
  Loader2,
  MapPin,
  Download,
  Calendar,
  Eye,
  ShoppingCart,
  Footprints,
  QrCode,
  RefreshCw,
  UserCheck,
  UserX,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sites, setSites] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [retailerPerformance, setRetailerPerformance] = useState([]);
  const [sitePerformance, setSitePerformance] = useState([]);
  const [trends, setTrends] = useState([]);
  const [qrEffectiveness, setQrEffectiveness] = useState(null);
  
  // Filters
  const [siteFilter, setSiteFilter] = useState("all");
  const [retailerFilter, setRetailerFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [siteFilter, retailerFilter, startDate, endDate, activeTab, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchSitesAndRetailers();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchSitesAndRetailers = async () => {
    try {
      const [sitesRes, retailersRes] = await Promise.all([
        axios.get(`${API}/sites`, { withCredentials: true }),
        axios.get(`${API}/retailers`, { withCredentials: true })
      ]);
      setSites(sitesRes.data);
      setRetailers(retailersRes.data);
    } catch (error) {
      console.error("Failed to fetch sites/retailers:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (siteFilter !== "all") params.append("site_id", siteFilter);
      if (retailerFilter !== "all") params.append("retailer_id", retailerFilter);
      if (startDate) params.append("start_date", `${startDate}T00:00:00`);
      if (endDate) params.append("end_date", `${endDate}T23:59:59`);
      
      const queryString = params.toString();
      
      // Always fetch summary
      try {
        const summaryRes = await axios.get(`${API}/analytics/summary?${queryString}`, { withCredentials: true });
        setSummary(summaryRes.data);
      } catch (err) {
        console.warn("Summary fetch failed, using empty state");
        setSummary({
          total_visits: 0,
          identified_visits: 0,
          anonymous_visits: 0,
          rewardable_visits: 0,
          rewarded_visits: 0,
          pos_visits: 0,
          footfall_visits: 0,
          repeat_visitors: 0,
          unique_visitors: 0,
          pos_footfall_ratio: 0
        });
      }
      
      // Fetch tab-specific data with individual error handling
      if (activeTab === "overview" || activeTab === "retailers") {
        try {
          const retailerRes = await axios.get(`${API}/analytics/retailer-performance?${queryString}`, { withCredentials: true });
          setRetailerPerformance(retailerRes.data || []);
        } catch (err) {
          console.warn("Retailer performance fetch failed");
          setRetailerPerformance([]);
        }
      }
      
      if (activeTab === "overview" || activeTab === "sites") {
        try {
          const siteRes = await axios.get(`${API}/analytics/site-performance?${queryString}`, { withCredentials: true });
          setSitePerformance(siteRes.data || []);
        } catch (err) {
          console.warn("Site performance fetch failed");
          setSitePerformance([]);
        }
      }
      
      if (activeTab === "trends") {
        try {
          const trendsRes = await axios.get(`${API}/analytics/trends?${queryString}`, { withCredentials: true });
          setTrends(trendsRes.data || []);
        } catch (err) {
          console.warn("Trends fetch failed");
          setTrends([]);
        }
      }
      
      if (activeTab === "qr") {
        try {
          const qrRes = await axios.get(`${API}/analytics/qr-effectiveness?${queryString}`, { withCredentials: true });
          setQrEffectiveness(qrRes.data || { qr_codes: [], type_summary: {} });
        } catch (err) {
          console.warn("QR effectiveness fetch failed");
          setQrEffectiveness({ qr_codes: [], type_summary: {} });
        }
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      // Don't show error toast - gracefully handle with empty states
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const params = new URLSearchParams();
      params.append("export_type", type);
      if (siteFilter !== "all") params.append("site_id", siteFilter);
      if (retailerFilter !== "all") params.append("retailer_id", retailerFilter);
      if (startDate) params.append("start_date", `${startDate}T00:00:00`);
      if (endDate) params.append("end_date", `${endDate}T23:59:59`);
      
      const response = await axios.get(`${API}/analytics/export?${params.toString()}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${type}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const clearFilters = () => {
    setSiteFilter("all");
    setRetailerFilter("all");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="admin-container min-h-screen" data-testid="admin-analytics">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
          
          {/* Commercial Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Commercial</p>
            <Link to="/admin/commercial" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Wallet className="w-5 h-5" /><span>Commercial Dashboard</span>
            </Link>
            <Link to="/admin/commercial/finance" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <DollarSign className="w-5 h-5" /><span>Finance Centre</span>
            </Link>
            <Link to="/admin/commercial/products-tariffs" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <Package className="w-5 h-5" /><span>Products & Tariffs</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Retail Analytics
            </h1>
            <p className="text-slate-400">Visit performance, retailer insights, and QR effectiveness</p>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-slate-600 text-slate-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6 p-4 bg-slate-800/50 rounded-xl">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-slate-400" />
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white" data-testid="site-filter">
                <SelectValue placeholder="All Sites" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all" className="text-white">All Sites</SelectItem>
                {sites.map(site => (
                  <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                    {site.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-slate-400" />
            <Select value={retailerFilter} onValueChange={setRetailerFilter}>
              <SelectTrigger className="w-[180px] bg-slate-900 border-slate-700 text-white" data-testid="retailer-filter">
                <SelectValue placeholder="All Retailers" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all" className="text-white">All Retailers</SelectItem>
                {retailers.map(r => (
                  <SelectItem key={r.retailer_id} value={r.retailer_id} className="text-white">
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-400" />
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[150px] bg-slate-900 border-slate-700 text-white"
              placeholder="Start date"
            />
            <span className="text-slate-500">to</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[150px] bg-slate-900 border-slate-700 text-white"
              placeholder="End date"
            />
          </div>
          
          {(siteFilter !== "all" || retailerFilter !== "all" || startDate || endDate) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-400 hover:text-white"
            >
              Clear filters
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors"
              onClick={() => setActiveTab("trends")}
              data-testid="kpi-total-visits"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Total Visits</p>
                    <p className="text-2xl font-bold text-white">{summary.total_visits}</p>
                  </div>
                  <Eye className="w-6 h-6 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-green-500/50 transition-colors"
              onClick={() => setActiveTab("retailers")}
              data-testid="kpi-identified-visits"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Identified</p>
                    <p className="text-2xl font-bold text-green-400">{summary.identified_visits}</p>
                  </div>
                  <UserCheck className="w-6 h-6 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-slate-500/50 transition-colors"
              onClick={() => setActiveTab("retailers")}
              data-testid="kpi-anonymous-visits"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Anonymous</p>
                    <p className="text-2xl font-bold text-slate-400">{summary.anonymous_visits}</p>
                  </div>
                  <UserX className="w-6 h-6 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-amber-500/50 transition-colors"
              onClick={() => navigate("/admin/rewards")}
              data-testid="kpi-rewardable-visits"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Rewardable</p>
                    <p className="text-2xl font-bold text-amber-400">{summary.rewardable_visits}</p>
                  </div>
                  <Gift className="w-6 h-6 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
              onClick={() => navigate("/admin/rewards")}
              data-testid="kpi-rewarded-visits"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Rewarded</p>
                    <p className="text-2xl font-bold text-purple-400">{summary.rewarded_visits}</p>
                  </div>
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </CardContent>
            </Card>
            <Card 
              className="admin-card border-slate-800 cursor-pointer hover:border-cyan-500/50 transition-colors"
              onClick={() => setActiveTab("sites")}
              data-testid="kpi-repeat-visitors"
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-400">Repeat Visitors</p>
                    <p className="text-2xl font-bold text-cyan-400">{summary.repeat_visitors}</p>
                  </div>
                  <Users className="w-6 h-6 text-cyan-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800">
            <TabsTrigger value="overview" className="data-[state=active]:bg-slate-700">Overview</TabsTrigger>
            <TabsTrigger value="retailers" className="data-[state=active]:bg-slate-700">Retailers</TabsTrigger>
            <TabsTrigger value="sites" className="data-[state=active]:bg-slate-700">Sites</TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-slate-700">Trends</TabsTrigger>
            <TabsTrigger value="qr" className="data-[state=active]:bg-slate-700">QR Effectiveness</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-6">
                {/* POS vs Footfall */}
                <div className="grid grid-cols-2 gap-4">
                  <Card 
                    className="admin-card border-slate-800 cursor-pointer hover:border-green-500/50 transition-colors"
                    onClick={() => setActiveTab("qr")}
                    data-testid="kpi-pos-visits"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                        <ShoppingCart className="w-4 h-4 text-green-400" />
                        POS Visits
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-green-400">{summary?.pos_visits || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">Purchase-related visits</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className="admin-card border-slate-800 cursor-pointer hover:border-purple-500/50 transition-colors"
                    onClick={() => setActiveTab("qr")}
                    data-testid="kpi-footfall-visits"
                  >
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                        <Footprints className="w-4 h-4 text-purple-400" />
                        Footfall Visits
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-purple-400">{summary?.footfall_visits || 0}</p>
                      <p className="text-xs text-slate-500 mt-1">General footfall visits</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Retailers Quick View */}
                <Card className="admin-card border-slate-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">Top Retailers</CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setActiveTab("retailers")}
                      className="text-slate-400"
                    >
                      View all
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-800">
                          <TableHead className="text-slate-400">Retailer</TableHead>
                          <TableHead className="text-slate-400 text-right">Total</TableHead>
                          <TableHead className="text-slate-400 text-right">Identified</TableHead>
                          <TableHead className="text-slate-400 text-right">Rewarded</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retailerPerformance.slice(0, 5).map(r => (
                          <TableRow key={r.retailer_id} className="border-slate-800">
                            <TableCell className="text-white font-medium">{r.retailer_name}</TableCell>
                            <TableCell className="text-right text-slate-300">{r.total_visits}</TableCell>
                            <TableCell className="text-right text-green-400">{r.identified_visits}</TableCell>
                            <TableCell className="text-right text-purple-400">{r.rewarded_visits}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Retailers Tab */}
              <TabsContent value="retailers">
                <Card className="admin-card border-slate-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">Retailer Performance</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport("retailer")}
                      className="border-slate-600 text-slate-300"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-800">
                          <TableHead className="text-slate-400">Retailer</TableHead>
                          <TableHead className="text-slate-400">Site</TableHead>
                          <TableHead className="text-slate-400 text-right">Total</TableHead>
                          <TableHead className="text-slate-400 text-right">Identified</TableHead>
                          <TableHead className="text-slate-400 text-right">Anonymous</TableHead>
                          <TableHead className="text-slate-400 text-right">POS</TableHead>
                          <TableHead className="text-slate-400 text-right">Footfall</TableHead>
                          <TableHead className="text-slate-400 text-right">Rewardable</TableHead>
                          <TableHead className="text-slate-400 text-right">Rewarded</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {retailerPerformance.map(r => (
                          <TableRow key={r.retailer_id} className="border-slate-800">
                            <TableCell className="text-white font-medium">{r.retailer_name}</TableCell>
                            <TableCell className="text-slate-300">{r.site_name}</TableCell>
                            <TableCell className="text-right text-white font-medium">{r.total_visits}</TableCell>
                            <TableCell className="text-right text-green-400">{r.identified_visits}</TableCell>
                            <TableCell className="text-right text-slate-400">{r.anonymous_visits}</TableCell>
                            <TableCell className="text-right text-green-300">{r.pos_visits}</TableCell>
                            <TableCell className="text-right text-purple-300">{r.footfall_visits}</TableCell>
                            <TableCell className="text-right text-amber-400">{r.rewardable_visits}</TableCell>
                            <TableCell className="text-right text-purple-400">{r.rewarded_visits}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Sites Tab */}
              <TabsContent value="sites">
                <Card className="admin-card border-slate-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">Site Performance</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport("site")}
                      className="border-slate-600 text-slate-300"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-800">
                          <TableHead className="text-slate-400">Site</TableHead>
                          <TableHead className="text-slate-400 text-right">Total</TableHead>
                          <TableHead className="text-slate-400 text-right">Identified</TableHead>
                          <TableHead className="text-slate-400 text-right">Anonymous</TableHead>
                          <TableHead className="text-slate-400 text-right">POS</TableHead>
                          <TableHead className="text-slate-400 text-right">Footfall</TableHead>
                          <TableHead className="text-slate-400 text-right">Unique</TableHead>
                          <TableHead className="text-slate-400 text-right">Repeat</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sitePerformance.map(s => (
                          <TableRow key={s.site_id} className="border-slate-800">
                            <TableCell className="text-white font-medium">{s.site_name}</TableCell>
                            <TableCell className="text-right text-white font-medium">{s.total_visits}</TableCell>
                            <TableCell className="text-right text-green-400">{s.identified_visits}</TableCell>
                            <TableCell className="text-right text-slate-400">{s.anonymous_visits}</TableCell>
                            <TableCell className="text-right text-green-300">{s.pos_visits}</TableCell>
                            <TableCell className="text-right text-purple-300">{s.footfall_visits}</TableCell>
                            <TableCell className="text-right text-blue-400">{s.unique_visitors}</TableCell>
                            <TableCell className="text-right text-cyan-400">{s.repeat_visitors}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Trends Tab */}
              <TabsContent value="trends">
                <Card className="admin-card border-slate-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-white">Daily Trends</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleExport("trends")}
                      className="border-slate-600 text-slate-300"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Export CSV
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {trends.length === 0 ? (
                      <p className="text-slate-400 text-center py-8">No trend data available for the selected period</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-slate-800">
                            <TableHead className="text-slate-400">Date</TableHead>
                            <TableHead className="text-slate-400 text-right">Total</TableHead>
                            <TableHead className="text-slate-400 text-right">Identified</TableHead>
                            <TableHead className="text-slate-400 text-right">Anonymous</TableHead>
                            <TableHead className="text-slate-400 text-right">Rewarded</TableHead>
                            <TableHead className="text-slate-400 text-right">POS</TableHead>
                            <TableHead className="text-slate-400 text-right">Footfall</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {trends.map(t => (
                            <TableRow key={t.date} className="border-slate-800">
                              <TableCell className="text-white font-medium">{t.date}</TableCell>
                              <TableCell className="text-right text-white">{t.total_visits}</TableCell>
                              <TableCell className="text-right text-green-400">{t.identified_visits}</TableCell>
                              <TableCell className="text-right text-slate-400">{t.anonymous_visits}</TableCell>
                              <TableCell className="text-right text-purple-400">{t.rewarded_visits}</TableCell>
                              <TableCell className="text-right text-green-300">{t.pos_visits}</TableCell>
                              <TableCell className="text-right text-purple-300">{t.footfall_visits}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* QR Effectiveness Tab */}
              <TabsContent value="qr">
                {qrEffectiveness && (
                  <div className="space-y-6">
                    {/* QR Type Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="admin-card border-slate-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                            <ShoppingCart className="w-4 h-4 text-green-400" />
                            POS QR Codes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-3xl font-bold text-green-400">{qrEffectiveness.type_summary.pos.total_scans}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {qrEffectiveness.type_summary.pos.rewardable_rate}% rewardable rate
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="admin-card border-slate-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-slate-400 flex items-center gap-2">
                            <Footprints className="w-4 h-4 text-purple-400" />
                            Footfall QR Codes
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-3xl font-bold text-purple-400">{qrEffectiveness.type_summary.footfall.total_scans}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {qrEffectiveness.type_summary.footfall.rewardable_rate}% rewardable rate
                          </p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* QR Codes Table */}
                    <Card className="admin-card border-slate-800">
                      <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-white">QR Code Performance</CardTitle>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleExport("qr")}
                          className="border-slate-600 text-slate-300"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Export CSV
                        </Button>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow className="border-slate-800">
                              <TableHead className="text-slate-400">QR Code</TableHead>
                              <TableHead className="text-slate-400">Retailer</TableHead>
                              <TableHead className="text-slate-400">Type</TableHead>
                              <TableHead className="text-slate-400 text-right">Total Scans</TableHead>
                              <TableHead className="text-slate-400 text-right">Identified</TableHead>
                              <TableHead className="text-slate-400 text-right">Rewardable</TableHead>
                              <TableHead className="text-slate-400 text-right">Rate</TableHead>
                              <TableHead className="text-slate-400">Status</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {qrEffectiveness.qr_codes.map(qr => (
                              <TableRow key={qr.qr_code_id} className="border-slate-800">
                                <TableCell className="font-mono text-slate-300 text-xs">{qr.qr_code_id}</TableCell>
                                <TableCell className="text-white">{qr.retailer_name}</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    qr.code_type === 'pos' 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {qr.code_type === 'pos' ? 'POS' : 'Footfall'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-right text-white font-medium">{qr.total_scans}</TableCell>
                                <TableCell className="text-right text-green-400">{qr.identified_scans}</TableCell>
                                <TableCell className="text-right text-amber-400">{qr.rewardable_scans}</TableCell>
                                <TableCell className="text-right text-purple-400">{qr.rewardable_rate}%</TableCell>
                                <TableCell>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    qr.is_active 
                                      ? 'bg-green-500/20 text-green-400' 
                                      : 'bg-slate-500/20 text-slate-400'
                                  }`}>
                                    {qr.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>
            </>
          )}
        </Tabs>
      </main>
    </div>
  );
}
